import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Users, Search, ChevronRight, Plus, Upload, Tags, Filter, Settings2, ChevronDown, MailX, RefreshCcw } from 'lucide-react';
import { V1Layout } from "../../components/v1/V1Layout";
import { Card, CardContent } from "../../../../ui/card";
import { Button } from "../../../../ui/button";
import { Input } from "../../../../ui/input";
import { Badge } from "../../../../ui/badge";
import { Checkbox } from '../../../../ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../ui/popover';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../../../ui/dropdown-menu';
import { Label } from '../../../../ui/label';
import { epicengageSupabase } from '../../../../../integrations/supabase/client';
import { useMerchant } from '../../../../../hooks/useMerchant';
import { toast } from "sonner";
import type { Customer, CustomerStatus } from "../../../../../types/epicengage";
import { format, formatDistanceToNow } from "date-fns";
import { CSVImportWizard } from '../../components/customers/CSVImportWizard';
import { ManageTagsDialog } from '../../components/customers/ManageTagsDialog';
import { ManageSegmentsDialog } from '../../components/customers/ManageSegmentsDialog';
import { BulkActionsBar } from '../../components/customers/BulkActionsBar';

import { apiUrl, clientheaderconfig } from "../../../../Helpers/Config";
import axios from "axios";

const PAGE_SIZE = 50;

interface CustomerRow {
  id: string;
  merchant_id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  name: string | null;
  phone: string | null;
  created_at: string;
  last_activity_at: string | null;
  status_tag: CustomerStatus;
  tags?: { id: string; name: string; color: string | null }[];
  is_unsubscribed?: boolean;
}

interface Tag { id: string; name: string; color: string | null; }
interface Segment { id: string; name: string; description: string | null; member_count?: number; }

type SelectionMode = 'none' | 'visible' | 'all';

export default function Customers() {
  const { merchant } = useMerchant();
  const [customers, setCustomers] = useState<CustomerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<CustomerStatus | 'ALL' | 'UNSUBSCRIBED'>('ALL');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showImportWizard, setShowImportWizard] = useState(false);
  const [showManageTags, setShowManageTags] = useState(false);
  const [showManageSegments, setShowManageSegments] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ email: '', first_name: '', last_name: '' });
  const [tags, setTags] = useState<Tag[]>([]);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [selectedSegmentId, setSelectedSegmentId] = useState<string>('ALL');
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Selection
  const [selectionMode, setSelectionMode] = useState<SelectionMode>('none');
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<string[]>([]);
  const [excludedIds, setExcludedIds] = useState<string[]>([]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const rangeStart = (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, totalCount);

  const selectedCount = selectionMode === 'all'
    ? totalCount - excludedIds.length
    : selectedCustomerIds.length;

  const fetchTags = useCallback(async () => {
    if (!merchant) return;
    const { data } = await epicengageSupabase
      .from('tags')
      .select('*')
      .eq('merchant_id', merchant.id)
      .order('name');
    setTags((data as Tag[]) || []);
  }, [merchant]);

  const fetchSegments = useCallback(async () => {
    if (!merchant) return;
    const { data: segs } = await epicengageSupabase
      .from('segments')
      .select('*')
      .eq('merchant_id', merchant.id)
      .order('name');

    if (segs) {
      const withCounts = await Promise.all(
        segs.map(async (s: any) => {
          const { count } = await epicengageSupabase
            .from('customer_segments')
            .select('*', { count: 'exact', head: true })
            .eq('segment_id', s.id);
          return { ...s, member_count: count || 0 };
        })
      );
      setSegments(withCounts as Segment[]);
    }
  }, [merchant]);

  // Apply common filters to a query
  const applyFilters = useCallback(<T extends { or: Function; eq: Function }>(query: T): T => {
    let q = query;
    if (search) {
      q = q.or(`email.ilike.%${search}%,first_name.ilike.%${search}%,last_name.ilike.%${search}%,name.ilike.%${search}%,phone.ilike.%${search}%`);
    }
    if (statusFilter !== 'ALL' && statusFilter !== 'UNSUBSCRIBED') {
      q = q.eq('status_tag', statusFilter);
    }
    return q;
  }, [search, statusFilter]);

  const fetchCustomers = useCallback(async () => {
    if (!merchant) return;
    setLoading(true);

    // Get IDs filtered by segment/tags first (if needed)
    let segmentFilterIds: Set<string> | null = null;
    let tagFilterIds: Set<string> | null = null;

    if (selectedSegmentId !== 'ALL') {
      const { data: memberIds } = await epicengageSupabase
        .from('customer_segments')
        .select('customer_id')
        .eq('segment_id', selectedSegmentId);
      segmentFilterIds = new Set((memberIds || []).map((m: any) => m.customer_id));
    }

    if (selectedTagIds.length > 0) {
      const { data: taggedIds } = await epicengageSupabase
        .from('customer_tags')
        .select('customer_id')
        .in('tag_id', selectedTagIds);
      tagFilterIds = new Set((taggedIds || []).map((m: any) => m.customer_id));
    }

    // Build base query
    let query = applyFilters(
      epicengageSupabase.from('customers').select('*').eq('merchant_id', merchant.id)
    ).order('created_at', { ascending: false });

    // Apply segment/tag filters via IN clause
    if (segmentFilterIds) {
      const ids = Array.from(segmentFilterIds);
      if (ids.length === 0) {
        setCustomers([]);
        setTotalCount(0);
        setLoading(false);
        return;
      }
      query = query.in('id', ids);
    }

    if (tagFilterIds) {
      const ids = Array.from(tagFilterIds);
      if (ids.length === 0) {
        setCustomers([]);
        setTotalCount(0);
        setLoading(false);
        return;
      }
      query = query.in('id', ids);
    }

    // Get total count for this filtered set
    let countQuery = applyFilters(
      epicengageSupabase.from('customers').select('*', { count: 'exact', head: true }).eq('merchant_id', merchant.id)
    );
    if (segmentFilterIds) countQuery = countQuery.in('id', Array.from(segmentFilterIds));
    if (tagFilterIds) countQuery = countQuery.in('id', Array.from(tagFilterIds));
    const { count } = await countQuery;
    setTotalCount(count || 0);

    // Apply pagination
    const from = (page - 1) * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;
    query = query.range(from, to);

    const { data, error } = await query;
    if (error) { console.error('Error fetching customers:', error); setLoading(false); return; }

    let customerList = (data || []) as CustomerRow[];

    // Fetch tags and suppressions for displayed customers
    const customerIds = customerList.map((c) => c.id);
    if (customerIds.length > 0) {
      const [ctData, supData] = await Promise.all([
        epicengageSupabase.from('customer_tags').select('customer_id, tag_id').in('customer_id', customerIds),
        epicengageSupabase.from('customer_suppressions').select('customer_id').eq('merchant_id', merchant.id).eq('channel', 'EMAIL').in('customer_id', customerIds),
      ]);

      const tagMap = new Map<string, string[]>();
      for (const ct of ctData.data || []) {
        const list = tagMap.get((ct as any).customer_id) || [];
        list.push((ct as any).tag_id);
        tagMap.set((ct as any).customer_id, list);
      }

      const suppressedSet = new Set((supData.data || []).map((s: any) => s.customer_id));

      customerList = customerList.map((c) => ({
        ...c,
        is_unsubscribed: suppressedSet.has(c.id),
        tags: (tagMap.get(c.id) || [])
          .map((tid) => tags.find((t) => t.id === tid))
          .filter(Boolean) as Tag[],
      }));
    }

    // Apply UNSUBSCRIBED filter client-side (since it's from a separate table)
    if (statusFilter === 'UNSUBSCRIBED') {
      customerList = customerList.filter(c => c.is_unsubscribed);
      // Adjust total count for unsubscribed filter
      const { count: unsubCount } = await epicengageSupabase
        .from('customer_suppressions')
        .select('*', { count: 'exact', head: true })
        .eq('merchant_id', merchant.id)
        .eq('channel', 'EMAIL');
      setTotalCount(unsubCount || 0);
    }

    setCustomers(customerList);
    setLoading(false);
  }, [merchant, search, statusFilter, selectedSegmentId, selectedTagIds, tags, page, applyFilters]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
    clearSelection();
  }, [search, statusFilter, selectedSegmentId, selectedTagIds]);

  useEffect(() => { fetchTags(); fetchSegments(); }, [fetchTags, fetchSegments]);
  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  const handleAddCustomer = async () => {
    if (!merchant || !newCustomer.email) return;
    const { data: existing } = await epicengageSupabase
      .from('customers')
      .select('id')
      .eq('merchant_id', merchant.id)
      .ilike('email', newCustomer.email.trim())
      .limit(1);

    if (existing && existing.length > 0) {
      toast.error('A customer with this email already exists');
      return;
    }

    const { error } = await epicengageSupabase
      .from('customers')
      .insert({
        merchant_id: merchant.id,
        email: newCustomer.email.trim().toLowerCase(),
        first_name: newCustomer.first_name || null,
        last_name: newCustomer.last_name || null,
        status_tag: 'NEW',
      });

    if (error) { toast.error('Failed to add customer: ' + error.message); return; }
    toast.success('Customer added successfully');
    setShowAddDialog(false);
    setNewCustomer({ email: '', first_name: '', last_name: '' });
    fetchCustomers();
  };

  // Selection helpers
  const clearSelection = () => {
    setSelectionMode('none');
    setSelectedCustomerIds([]);
    setExcludedIds([]);
  };

  const selectVisible = () => {
    setSelectionMode('visible');
    setSelectedCustomerIds(customers.map((c) => c.id));
    setExcludedIds([]);
  };

  const selectAll = () => {
    setSelectionMode('all');
    setSelectedCustomerIds([]);
    setExcludedIds([]);
  };

  const isRowSelected = (id: string) => {
    if (selectionMode === 'all') return !excludedIds.includes(id);
    return selectedCustomerIds.includes(id);
  };

  const toggleCustomer = (id: string) => {
    if (selectionMode === 'all') {
      setExcludedIds((prev) =>
        prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
      );
    } else {
      const newSelected = selectedCustomerIds.includes(id)
        ? selectedCustomerIds.filter((i) => i !== id)
        : [...selectedCustomerIds, id];
      setSelectedCustomerIds(newSelected);
      setSelectionMode(newSelected.length > 0 ? 'visible' : 'none');
    }
  };

  const getStatusBadge = (status: CustomerStatus, isUnsubscribed?: boolean) => {
    if (isUnsubscribed) {
      return <Badge variant="destructive" className="gap-1"><MailX className="h-3 w-3" />Unsubscribed</Badge>;
    }
    switch (status) {
      case 'NEW': return <Badge variant="secondary">New</Badge>;
      case 'ACTIVE': return <Badge variant="default">Active</Badge>;
      case 'INACTIVE': return <Badge variant="outline">Inactive</Badge>;
    }
  };

  const getDisplayName = (c: CustomerRow) => {
    if (c.name) return c.name;
    if (c.first_name || c.last_name) return `${c.first_name || ''} ${c.last_name || ''}`.trim();
    return 'No name';
  };

  // Build filter context for BulkActionsBar
  const filterContext = {
    search,
    statusFilter,
    selectedSegmentId,
    selectedTagIds,
  };

  // Pagination page numbers
  const getPageNumbers = () => {
    const pages: (number | 'ellipsis')[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (page > 3) pages.push('ellipsis');
      for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
        pages.push(i);
      }
      if (page < totalPages - 2) pages.push('ellipsis');
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <V1Layout>
      <div>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Users className="h-8 w-8" />
              Customers
            </h1>
            <p className="text-muted-foreground mt-1">Manage your customer list and view their timeline</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowImportWizard(true)}>
              <Upload className="h-4 w-4 mr-2" />Import CSV
            </Button>
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button><Plus className="h-4 w-4 mr-2" />Add Customer</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Add Customer</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={newCustomer.email}
                      onChange={(e) => setNewCustomer((c) => ({ ...c, email: e.target.value }))}
                      placeholder="customer@example.com"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>First Name</Label>
                      <Input
                        value={newCustomer.first_name}
                        onChange={(e) => setNewCustomer((c) => ({ ...c, first_name: e.target.value }))}
                        placeholder="John"
                      />
                    </div>
                    <div>
                      <Label>Last Name</Label>
                      <Input
                        value={newCustomer.last_name}
                        onChange={(e) => setNewCustomer((c) => ({ ...c, last_name: e.target.value }))}
                        placeholder="Doe"
                      />
                    </div>
                  </div>
                  <Button onClick={handleAddCustomer} className="w-full">Add Customer</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectionMode !== 'none' && selectedCount > 0 && (
          <div className="mb-4">
            <BulkActionsBar
              selectionMode={selectionMode}
              selectedIds={selectedCustomerIds}
              excludedIds={excludedIds}
              totalCount={totalCount}
              filterContext={filterContext}
              onClear={clearSelection}
              tags={tags}
              segments={segments}
              onDone={() => { clearSelection(); fetchCustomers(); fetchTags(); fetchSegments(); }}
            />
          </div>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-4">
            <div className="flex gap-4 flex-wrap">
              <div className="flex-1 min-w-[200px] relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, email, or phone..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>

              {/* Status */}
              <div className="flex gap-1">
                {(['ALL', 'NEW', 'ACTIVE', 'INACTIVE', 'UNSUBSCRIBED'] as const).map((status) => (
                  <Button
                    key={status}
                    variant={statusFilter === status ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setStatusFilter(status)}
                  >
                    {status === 'ALL' ? 'All' : status === 'UNSUBSCRIBED' ? 'Unsubscribed' : status.charAt(0) + status.slice(1).toLowerCase()}
                  </Button>
                ))}
              </div>

              {/* Segment filter */}
              <Select value={selectedSegmentId} onValueChange={setSelectedSegmentId}>
                <SelectTrigger className="w-44">
                  <Filter className="h-3 w-3 mr-1" />
                  <SelectValue placeholder="All Customers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Customers</SelectItem>
                  {segments.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name} ({s.member_count})</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Tag filter */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Tags className="h-3 w-3 mr-1" />
                    Tags {selectedTagIds.length > 0 && `(${selectedTagIds.length})`}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-48 p-2">
                  {tags.map((t) => (
                    <label key={t.id} className="flex items-center gap-2 p-1 cursor-pointer text-sm">
                      <Checkbox
                        checked={selectedTagIds.includes(t.id)}
                        onCheckedChange={() =>
                          setSelectedTagIds((ids) =>
                            ids.includes(t.id) ? ids.filter((i) => i !== t.id) : [...ids, t.id]
                          )
                        }
                      />
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: t.color || '#888' }} />
                      {t.name}
                    </label>
                  ))}
                  {tags.length === 0 && <p className="text-xs text-muted-foreground p-2">No tags</p>}
                  {selectedTagIds.length > 0 && (
                    <Button size="sm" variant="ghost" className="w-full mt-1" onClick={() => setSelectedTagIds([])}>
                      Clear
                    </Button>
                  )}
                </PopoverContent>
              </Popover>

              {/* Management buttons */}
              <Button variant="ghost" size="sm" onClick={() => setShowManageTags(true)}>
                <Settings2 className="h-3 w-3 mr-1" />Tags
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setShowManageSegments(true)}>
                <Settings2 className="h-3 w-3 mr-1" />Segments
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Customer Table */}
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="flex items-center gap-1 p-1 rounded hover:bg-muted">
                        <Checkbox
                          checked={selectionMode === 'all' || (selectionMode === 'visible' && selectedCustomerIds.length === customers.length && customers.length > 0)}
                          className="pointer-events-none"
                        />
                        <ChevronDown className="h-3 w-3 text-muted-foreground" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="bg-popover">
                      <DropdownMenuItem onClick={selectVisible}>
                        Select visible ({customers.length})
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={selectAll}>
                        Select all contacts ({totalCount.toLocaleString()})
                      </DropdownMenuItem>
                      {selectionMode !== 'none' && (
                        <DropdownMenuItem onClick={clearSelection}>
                          Clear selection
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Tags</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead>Last Activity</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {/* Selection info banner */}
              {selectionMode === 'all' && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-2 bg-primary/5 text-sm">
                    All {(totalCount - excludedIds.length).toLocaleString()} contacts matching current filter are selected.
                    <Button variant="link" size="sm" className="ml-2" onClick={clearSelection}>Clear selection</Button>
                  </TableCell>
                </TableRow>
              )}
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    Loading customers...
                  </TableCell>
                </TableRow>
              ) : customers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No customers found
                  </TableCell>
                </TableRow>
              ) : (
                customers.map((customer) => (
                  <TableRow key={customer.id}>
                    <TableCell>
                      <Checkbox
                        checked={isRowSelected(customer.id)}
                        onCheckedChange={() => toggleCustomer(customer.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{getDisplayName(customer)}</div>
                        <div className="text-sm text-muted-foreground">{customer.email}</div>
                        {customer.phone && <div className="text-xs text-muted-foreground">{customer.phone}</div>}
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(customer.status_tag, customer.is_unsubscribed)}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(customer.tags || []).slice(0, 2).map((t) => (
                          <Badge
                            key={t.id}
                            variant="outline"
                            className="text-xs"
                            style={t.color ? { borderColor: t.color, color: t.color } : undefined}
                          >
                            {t.name}
                          </Badge>
                        ))}
                        {(customer.tags || []).length > 2 && (
                          <Badge variant="outline" className="text-xs">
                            +{(customer.tags || []).length - 2}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {format(new Date(customer.created_at), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {customer.last_activity_at
                        ? formatDistanceToNow(new Date(customer.last_activity_at), { addSuffix: true })
                        : 'Never'}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" asChild>
                        <Link to={`/clientpanel/enage/customers/${customer.id}`}>
                          View <ChevronRight className="h-4 w-4 ml-1" />
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {totalCount > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <p className="text-sm text-muted-foreground">
                Showing {rangeStart}–{rangeEnd} of {totalCount.toLocaleString()}
              </p>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </Button>
                {getPageNumbers().map((p, i) =>
                  p === 'ellipsis' ? (
                    <span key={`e-${i}`} className="px-2 text-muted-foreground">…</span>
                  ) : (
                    <Button
                      key={p}
                      variant={p === page ? 'default' : 'outline'}
                      size="sm"
                      className="min-w-[36px]"
                      onClick={() => setPage(p)}
                    >
                      {p}
                    </Button>
                  )
                )}
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Dialogs */}
      <CSVImportWizard
        open={showImportWizard}
        onOpenChange={setShowImportWizard}
        tags={tags}
        segments={segments}
        onImportComplete={() => { fetchCustomers(); fetchTags(); fetchSegments(); }}
      />
      <ManageTagsDialog
        open={showManageTags}
        onOpenChange={setShowManageTags}
        tags={tags}
        onTagsChange={fetchTags}
      />
      <ManageSegmentsDialog
        open={showManageSegments}
        onOpenChange={setShowManageSegments}
        segments={segments}
        onSegmentsChange={fetchSegments}
      />
    </V1Layout>
  );
}
